import { useStore } from '@nanostores/solid'
import {
  For,
  Show,
  Suspense,
  createMemo,
  createSignal,
  type JSX,
} from 'solid-js'
import arrowDownSvg from '~/assets/figma/arrow-down.svg'
import bakeryLogStampSvg from '~/assets/figma/bakery-log-stamp.svg'
import cabinetBottomSvg from '~/assets/figma/cabinet-bottom.svg'
import dividerPath1Svg from '~/assets/figma/divider-path-1.svg'
import dividerPath2Svg from '~/assets/figma/divider-path-2.svg'
import laserBgSvg from '~/assets/figma/laser-bg.svg'
import menuBoardSvg from '~/assets/figma/menu-board.svg'
import { cakeCollection, type CakeKind } from '~/config'
import { useLocalizedTreeName } from '~/hooks/use-localized-tree-name'
import { getCampaignItemTypeId } from '~/lib/campaign-item-type-id'
import { deriveBakeryLog, type BakeryLogEntry } from '~/lib/derive-bakery-log'
import { language } from '~/lib/params'
import { getRewardTreeAssets } from '~/lib/reward-tree-assets'
import { m } from '~/paraglide/messages'
import { useItems } from '~/resources/items'
import { $userInfo } from '~/store/user-info'
import { Spinner } from './spinner'

// paraglide locales use snake_case (e.g. zh_tw, pt_br); Intl needs BCP 47.
const bakeryLogDateFormatter = new Intl.DateTimeFormat(
  language.replace('_', '-'),
  {
    dateStyle: 'medium',
    timeStyle: 'short',
  },
)

type CakeSlot = {
  treeTypeId: number
  kind: CakeKind
  unlocked: boolean
}

const SLOT_REVEAL_BG =
  '[background-image:radial-gradient(60%_45%_at_50%_45%,#fef8e8_0%,#ffedbb_50%,#ffe28d_100%)]'

export function CollectionCabinet(props: { unlockedTreeIds: Set<number> }) {
  const slots = createMemo<CakeSlot[]>(() =>
    cakeCollection.map((cake) => ({
      treeTypeId: cake.treeTypeId,
      kind: cake.kind,
      unlocked: props.unlockedTreeIds.has(cake.treeTypeId),
    })),
  )
  const macarons = createMemo(() => slots().filter((s) => s.kind === 'macaron'))
  const cupcakes = createMemo(() =>
    slots().filter((s) => s.kind === 'cupcake' || s.kind === 'special'),
  )

  return (
    <div class="mx-auto flex w-full max-w-[640px] flex-col items-center gap-2 px-4">
      <div class="flex w-full flex-col items-center gap-0.5">
        <Cabinet macarons={macarons()} cupcakes={cupcakes()} />
        <CabinetBase />
      </div>
      <BakeryLogToggle />
    </div>
  )
}

function Cabinet(props: { macarons: CakeSlot[]; cupcakes: CakeSlot[] }) {
  return (
    <div class="relative flex w-full flex-col items-center gap-2 px-6 pb-6 pt-4">
      {/* OUTER wood frame — fills the whole cabinet. Stripes lightened. */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-0 rounded-[36px] bg-[#a46740]"
        style={{
          'background-image': [
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.04) 0 1px, transparent 1px 7px)',
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.03) 0 2px, transparent 2px 13px)',
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.05) 0 3px, transparent 3px 41px)',
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.02) 0 0.5px, transparent 0.5px 19px)',
            'repeating-linear-gradient(0deg, rgba(255,235,178,0.04) 0 1px, transparent 1px 11px)',
            'repeating-linear-gradient(0deg, rgba(255,235,178,0.03) 0 2px, transparent 2px 29px)',
          ].join(', '),
        }}
      />
      {/* OUTER wave layer — wood grain SVG. URL-encoded so Chrome parses
          unencoded `<` / `>` reliably. */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-0 overflow-hidden rounded-[36px]"
        style={{
          'background-image': `url("data:image/svg+xml,${encodeURIComponent(
            `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 48' width='200' height='48'>
              <path d='M0,8 Q25,2 50,8 T100,8 T150,8 T200,8' stroke='rgba(0,0,0,0.10)' stroke-width='1.5' fill='none'/>
              <path d='M0,22 Q35,30 70,22 T140,22 T200,22' stroke='rgba(0,0,0,0.08)' stroke-width='2' fill='none'/>
              <path d='M0,38 Q30,32 60,38 T120,38 T200,38' stroke='rgba(255,235,178,0.10)' stroke-width='1.5' fill='none'/>
            </svg>`,
          )}")`,
        }}
      />
      {/* INNER area — same wood, offset stripe positions for 3D plank seam */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-[6px_7px] overflow-hidden rounded-[32px] bg-[#a46740]"
        style={{
          'background-image': [
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.04) 0 1px, transparent 1px 7px)',
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.03) 0 2px, transparent 2px 13px)',
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.05) 0 3px, transparent 3px 41px)',
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.02) 0 0.5px, transparent 0.5px 19px)',
            'repeating-linear-gradient(0deg, rgba(255,235,178,0.04) 0 1px, transparent 1px 11px)',
            'repeating-linear-gradient(0deg, rgba(255,235,178,0.03) 0 2px, transparent 2px 29px)',
          ].join(', '),
          'background-position':
            '17px 5px, 23px 9px, 11px 13px, 7px 4px, 13px 7px, 19px 11px',
        }}
      />
      {/* INNER wave — second wood grain SVG with different period so outer
          and inner grain don't visually align. */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-[6px_7px] overflow-hidden rounded-[32px] opacity-80"
        style={{
          'background-image': `url("data:image/svg+xml,${encodeURIComponent(
            `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 56' width='240' height='56'>
              <path d='M0,12 Q40,4 80,12 T160,12 T240,12' stroke='rgba(0,0,0,0.10)' stroke-width='1.5' fill='none'/>
              <path d='M0,30 Q30,38 60,30 T120,30 T180,30 T240,30' stroke='rgba(0,0,0,0.07)' stroke-width='1.5' fill='none'/>
              <path d='M0,48 Q45,42 90,48 T180,48 T240,48' stroke='rgba(255,235,178,0.08)' stroke-width='1.5' fill='none'/>
            </svg>`,
          )}")`,
        }}
      />
      <div
        aria-hidden
        class="pointer-events-none absolute inset-[6px_7px] rounded-[32px] bg-[rgba(22,9,2,0.56)] [box-shadow:inset_0_0_0_5px_rgba(0,0,0,0.25)]"
      />
      <div
        aria-hidden
        class="pointer-events-none absolute inset-0 rounded-[36px] [box-shadow:inset_0_-3px_4px_0_rgba(0,0,0,0.25),inset_0_4px_4px_0_rgba(255,235,178,0.3)]"
      />

      <Shelf title={m.macarons_title()}>
        <For each={props.macarons}>
          {(slot) => (
            <SlotMacaron
              treeTypeId={slot.treeTypeId}
              unlocked={slot.unlocked}
            />
          )}
        </For>
      </Shelf>

      <ShelfDivider />

      <Shelf title={m.cupcakes_title()}>
        <For each={props.cupcakes}>
          {(slot) => (
            <SlotCupcake
              treeTypeId={slot.treeTypeId}
              unlocked={slot.unlocked}
              kind={slot.kind}
            />
          )}
        </For>
      </Shelf>
    </div>
  )
}

function Shelf(props: { title: string; children: JSX.Element }) {
  return (
    <div class="relative flex w-full flex-col items-center gap-2">
      <p class="text-sm font-bold leading-5 text-[#d9c0a4]">{props.title}</p>
      <div class="flex w-full flex-wrap items-center justify-center gap-1.5">
        {props.children}
      </div>
    </div>
  )
}

function ShelfDivider() {
  return (
    <div class="relative h-1 w-full">
      <div class="absolute inset-y-1/2 left-[5.62px] right-[6.19px] h-px -translate-y-1/2 bg-[#D9C0A4]" />
      <div class="absolute right-0 top-0 h-1 w-[7.309px] overflow-hidden">
        <img
          src={dividerPath1Svg}
          alt=""
          aria-hidden="true"
          class="block size-full -scale-x-100"
        />
      </div>
      <div class="absolute left-0 top-0 h-1 w-[6.747px] overflow-hidden">
        <img
          src={dividerPath2Svg}
          alt=""
          aria-hidden="true"
          class="block size-full -scale-x-100"
        />
      </div>
    </div>
  )
}

function CabinetBase() {
  return (
    <div class="flex items-center justify-center">
      <img
        src={cabinetBottomSvg}
        alt=""
        aria-hidden="true"
        class="h-5 w-[149px] -scale-y-100"
      />
    </div>
  )
}

function SlotMacaron(props: { treeTypeId: number; unlocked: boolean }) {
  const fullSrc = createMemo(() => getRewardTreeAssets(props.treeTypeId).full)
  return (
    <div
      class="relative flex size-[60px] shrink-0 flex-col items-center justify-center rounded-[12.5px] border px-1 pb-2 backdrop-blur-[3.9px]"
      classList={{
        [`border-[#efc586] ${SLOT_REVEAL_BG}`]: props.unlocked,
        'border-[#dbdbdb] bg-white/[0.37]': !props.unlocked,
      }}
    >
      <img
        src={fullSrc()}
        alt={props.unlocked ? 'Macaron (collected)' : 'Locked macaron'}
        class="aspect-square w-full object-contain"
        classList={{ 'grayscale brightness-[.2]': !props.unlocked }}
      />
    </div>
  )
}

function SlotCupcake(props: {
  treeTypeId: number
  unlocked: boolean
  kind: CakeKind
}) {
  return (
    <Show
      when={props.kind === 'special'}
      fallback={
        <SlotCupcakeRegular
          treeTypeId={props.treeTypeId}
          unlocked={props.unlocked}
        />
      }
    >
      <SlotSpecialCake
        treeTypeId={props.treeTypeId}
        unlocked={props.unlocked}
      />
    </Show>
  )
}

function SlotCupcakeRegular(props: { treeTypeId: number; unlocked: boolean }) {
  const fullSrc = createMemo(() => getRewardTreeAssets(props.treeTypeId).full)
  return (
    <div
      class="relative flex w-24 shrink-0 flex-col items-center rounded-xl border px-[3px] pb-2 backdrop-blur-[3.8px]"
      classList={{
        [`border-[#efc586] ${SLOT_REVEAL_BG}`]: props.unlocked,
        'border-[#dbdbdb] bg-white/[0.37]': !props.unlocked,
      }}
    >
      <img
        src={fullSrc()}
        alt={props.unlocked ? 'Cupcake (collected)' : 'Locked cupcake'}
        class="size-[63px] object-contain"
        classList={{ 'grayscale brightness-[.2]': !props.unlocked }}
      />
    </div>
  )
}

function SlotSpecialCake(props: { treeTypeId: number; unlocked: boolean }) {
  const fullSrc = createMemo(() => getRewardTreeAssets(props.treeTypeId).full)
  return (
    <Show
      when={props.unlocked}
      fallback={
        <div class="relative flex w-24 shrink-0 flex-col items-center rounded-xl border border-[#efc586] px-[3px] pb-2 backdrop-blur-[3.8px] [background:linear-gradient(180deg,rgba(164,159,68,0.29)_0%,rgba(62,60,26,0.29)_100%),rgba(255,255,255,0.37)]">
          <img
            src={fullSrc()}
            alt="Locked special cake"
            class="size-[63px] object-contain brightness-[.2] grayscale"
          />
        </div>
      }
    >
      <div class="relative flex w-24 shrink-0 flex-col items-center gap-[6.8px] overflow-hidden rounded-xl border border-[#efc586] px-[3px] pb-2 backdrop-blur-[3.8px] [background-image:radial-gradient(ellipse_at_center,#fff98e_0%,#fff56f_14.66%,#fff051_29.33%,#f6c948_57.69%,#eea23f_86.06%)]">
        <div class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none">
          <div class="animate-laser-spin">
            <img
              src={laserBgSvg}
              alt=""
              aria-hidden="true"
              class="block h-[70px] w-[86px] animate-laser-pulse"
            />
          </div>
        </div>
        <img
          src={fullSrc()}
          alt="Special cake (collected)"
          class="relative size-[63px] object-contain"
        />
      </div>
    </Show>
  )
}

function BakeryLogToggle() {
  const [open, setOpen] = createSignal(false)
  return (
    <div class="flex w-full flex-col items-stretch overflow-clip rounded-[25px] backdrop-blur-md">
      <button
        type="button"
        aria-expanded={open()}
        onClick={() => setOpen((o) => !o)}
        class="flex items-center justify-between bg-black/[0.6] p-3 text-left transition-colors active:bg-black/[0.75]"
      >
        <div class="flex items-center gap-3">
          <img
            src={menuBoardSvg}
            alt=""
            aria-hidden="true"
            class="size-5 [filter:brightness(0)_invert(1)]"
          />
          <span class="text-sm leading-5 text-white">
            {m.cake_box_history()}
          </span>
        </div>
        <span class="grid size-9 place-items-center rounded-full backdrop-blur-md">
          <img
            src={arrowDownSvg}
            alt=""
            aria-hidden="true"
            class="size-[18px] transition-transform duration-200 [filter:brightness(0)_invert(1)]"
            classList={{ 'rotate-180': open() }}
          />
        </span>
      </button>
      <Show when={open()}>
        <div class="bg-black/[0.6] px-3 pb-4 pt-3">
          <Suspense
            fallback={
              <div class="grid min-h-[80px] place-items-center">
                <Spinner class="size-5 text-white" />
              </div>
            }
          >
            <BakeryLogList />
          </Suspense>
        </div>
      </Show>
    </div>
  )
}

function BakeryLogList() {
  const itemsQuery = useItems()
  const userInfo = useStore($userInfo)
  const entries = createMemo(() => {
    const info = userInfo()
    if (!info) return []
    return deriveBakeryLog(itemsQuery.data ?? [], {
      campaignItemTypeId: getCampaignItemTypeId(info),
    })
  })
  return (
    <Show
      when={entries().length > 0}
      fallback={
        <p class="text-[12px] leading-4 text-white opacity-50">
          {m.cake_box_history_empty_state()}
        </p>
      }
    >
      <ul class="no-scrollbar flex max-h-[300px] flex-col gap-4 overflow-y-auto">
        <For each={entries()}>{(entry) => <BakeryLogRow entry={entry} />}</For>
      </ul>
    </Show>
  )
}

function BakeryLogRow(props: { entry: BakeryLogEntry }) {
  const treeName = useLocalizedTreeName(() =>
    props.entry.type === 'tree' ? props.entry.treeTypeId : 0,
  )
  const title = () => {
    if (props.entry.type === 'stamp') {
      return m.stamps_text({ number: String(props.entry.count) })
    }
    return treeName()
  }
  const lineText = () =>
    `${bakeryLogDateFormatter.format(new Date(props.entry.timestamp))} · via cake box`

  return (
    <li class="flex items-center gap-2">
      <div class="relative grid size-[42px] shrink-0 place-items-center">
        <Show
          when={props.entry.type === 'stamp'}
          fallback={
            <Show when={props.entry.type === 'tree' && props.entry.thumbSrc}>
              <img
                src={props.entry.type === 'tree' ? props.entry.thumbSrc : ''}
                alt=""
                aria-hidden="true"
                class="size-full object-contain"
              />
            </Show>
          }
        >
          <img
            src={bakeryLogStampSvg}
            alt=""
            aria-hidden="true"
            class="size-[25px]"
          />
          <Show when={props.entry.type === 'stamp'}>
            <span
              class="absolute bottom-[2px] right-[5px] text-[12px] font-bold tabular-nums leading-4 text-white"
              style={{
                '-webkit-text-stroke': '1.5px #2A1B3D',
                'paint-order': 'stroke fill',
              }}
            >
              {props.entry.type === 'stamp' ? props.entry.count : 0}
            </span>
          </Show>
        </Show>
      </div>
      <div class="flex min-w-0 flex-1 flex-col gap-[2px] text-white">
        <p class="truncate text-[12px] leading-4">{title()}</p>
        <p class="truncate text-[10px] leading-[14px] opacity-50">
          {lineText()}
        </p>
      </div>
    </li>
  )
}
